import api from 'services/api';
import { array, bool, enforce, nullable, num, object, optional, runSchema, str, unknownValue, withDefault } from 'validation';

import {
  outputMatcherSchema,
  parameterSchema,
  raiseEventSchema,
  retentionPolicySchema,
  waitEventSchema,
  type OutputMatcher,
  type Parameter,
  type RaiseEvent,
  type RetentionPolicy,
  type SaveNodeDefinition,
  type WaitEvent,
} from './types';

// POST /node_definitions — create. The POST route handler assembles the response record itself (upsert + its own folder-path
// enrichment), so `CreatedNodeDefinition` is redeclared in full rather than shared with the GET-one `NodeDefinition`: they
// are equal-by-coincidence, not identity (see ../../README.md). Structurally identical today, so structural typing lets a
// created record flow wherever a `NodeDefinition` is expected. Field invariants are documented on `getNodeDefinition`.
export interface CreatedNodeDefinition {
  readonly node_definition_id: number;
  readonly version: number;
  readonly name: string;
  readonly description: string | null;
  readonly type: string;
  readonly sub_type: string | null;
  readonly documentation: string | null;
  readonly zone_id: number;
  readonly zone_name: string;
  readonly folder_name: string | null;
  readonly default_queue_path: string | null;
  readonly restrict_to_scheduling_window: number | null;
  readonly auto_restart_enabled: boolean;
  readonly auto_restart_amount: number;
  readonly auto_restart_delay_seconds: number;
  readonly restart_on_return_codes: readonly number[];
  readonly hold_on_submit: boolean;
  readonly runtime_limit_min: number | null;
  readonly runtime_limit_max: number | null;
  readonly runtime_limit_min_hhmmss?: string;
  readonly runtime_limit_max_hhmmss?: string;
  readonly runtime_limit_event: number | null;
  readonly runtime_limit_terminate: boolean;
  readonly editable_handles: boolean;
  readonly locks: readonly string[];
  readonly parameters: readonly Parameter[];
  readonly wait_events: readonly WaitEvent[];
  readonly raise_events: readonly RaiseEvent[];
  readonly output_matchers: readonly OutputMatcher[];
  readonly retention_policies: readonly RetentionPolicy[];
  readonly config: unknown;
  readonly handles: readonly unknown[];
  readonly child_nodes: readonly unknown[];
  readonly child_edges: readonly unknown[];
}

const createdSchema = object<CreatedNodeDefinition>({
  node_definition_id: num(),
  version: num(),
  name: str(),
  description: nullable(str()),
  type: str(),
  sub_type: nullable(str()),
  documentation: nullable(str()),
  zone_id: num(),
  zone_name: str(),
  folder_name: nullable(str()),
  default_queue_path: nullable(str()),
  restrict_to_scheduling_window: nullable(num()),
  auto_restart_enabled: bool(),
  auto_restart_amount: num(),
  auto_restart_delay_seconds: num(),
  restart_on_return_codes: withDefault(array(num()), []),
  hold_on_submit: bool(),
  runtime_limit_min: nullable(num()),
  runtime_limit_max: nullable(num()),
  runtime_limit_min_hhmmss: optional(str()),
  runtime_limit_max_hhmmss: optional(str()),
  runtime_limit_event: nullable(num()),
  runtime_limit_terminate: bool(),
  editable_handles: bool(),
  locks: withDefault(array(str()), []),
  parameters: withDefault(array(parameterSchema), []),
  wait_events: withDefault(array(waitEventSchema), []),
  raise_events: withDefault(array(raiseEventSchema), []),
  output_matchers: withDefault(array(outputMatcherSchema), []),
  retention_policies: withDefault(array(retentionPolicySchema), []),
  config: unknownValue(),
  handles: withDefault(array(unknownValue()), []),
  child_nodes: withDefault(array(unknownValue()), []),
  child_edges: withDefault(array(unknownValue()), []),
});

/**
 * POST `/node_definitions`. Returns the created record. Throws on HTTP error (the caller handles the 400 path) or on
 * structural validation failure of the response.
 */
export const postNodeDefinition = async (save: SaveNodeDefinition): Promise<CreatedNodeDefinition> => {
  const res = await api.post('/node_definitions', save);
  const result = runSchema(createdSchema, res.data, 'postNodeDefinition');
  if (!result.ok) {
    throw new Error('postNodeDefinition: node definition response failed validation');
  }
  return enforce(result.value, result.warnings, ['node_definition_id', 'name', 'type', 'zone_name']);
};
